import { useEffect, useMemo, useState } from 'react';
import { FiInfo, FiX } from 'react-icons/fi';
import { GiSettingsKnobs } from 'react-icons/gi';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { PremiumIcon } from '../Navbar/Icons';
import { TacticsIds } from '../TacticPicker/icons';

import { ModalTacticChange } from './ModalTacticChange';

import {
  Button,
  Card,
  Divider,
  Loader,
  Modal,
  TacticPicker,
} from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import usePinsAccount from '@/hooks/usePinsAccount';
import { useTeamStore } from '@/hooks/useTeamStore';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { getTacticAssistant } from '@/services/team11';
import { EnumTeamContext } from '@/types/team';
import { cn, formatToBalanceArs } from '@/utils';
import 'react-tooltip/dist/react-tooltip.css';

export type TypesHeaderInfoTeam = {
  context?: EnumTeamContext;
  onClick?: () => void;
};

interface StateValue {
  showModalChanges: boolean;
  showModalTacticSelector: boolean;
  newTactic: TacticsIds;
}

export const HeaderMyTeam = ({ context, onClick }: TypesHeaderInfoTeam) => {
  const { isMobile, isDesktop } = useIsDesktop();
  const { state } = useTeamStore();
  const { pinsData, handleSetPinsData } = usePinsAccount();
  const { userInitialData } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [tacticAssistant, setTacticAssistant] = useState<StateValue>({
    showModalChanges: false,
    showModalTacticSelector: false,
    newTactic: 1,
  });

  const {
    mutateAsync: onHandleTacticModal,
    isLoading,
    data: tacticAssistantResponse,
  } = useMutation(
    async (tacticId: number) => await getTacticAssistant(tacticId),
    {
      onError: (err) => {
        const error = err as Error;
        toast.showError(error);
      },
    }
  );

  useEffect(() => {
    handleSetPinsData({
      ...pinsData,
      pinsAvailable: pinsData.pinsAvailable,
    });
  }, []);

  const isFilterTeamVariant =
    context &&
    [EnumTeamContext.FilterTeam11, EnumTeamContext.FilterTeam5].includes(
      context
    );

  const displayBalance = useMemo(() => {
    if (
      context === EnumTeamContext.Team11 ||
      context === EnumTeamContext.Team5
    ) {
      return state.balance && `$${formatToBalanceArs(state.balance)}`;
    }

    if (
      state.isBeforeVeda &&
      userInitialData &&
      context === EnumTeamContext.FilterTeam11 &&
      state.balance
    ) {
      if (state.selectedPlayerOnTeam && state.selectedPlayerOnTeam?.cot) {
        return `$${formatToBalanceArs(
          userInitialData.presupuesto_total -
            state.balance +
            state.selectedPlayerOnTeam?.cot * 10000
        )}`;
      }
      return `$${formatToBalanceArs(
        userInitialData.presupuesto_total - state.balance
      )}`;
    }

    if (
      userInitialData &&
      context === EnumTeamContext.FilterTeam11 &&
      state.balance &&
      state.selectedPlayerOnTeam
    ) {
      return `$${formatToBalanceArs(
        userInitialData.presupuesto_total -
          state.balance +
          state.selectedPlayerOnTeam?.cot * 10000
      )}`;
    }

    if (context === EnumTeamContext.FilterTeam5) {
      return 'ilimitado';
    }
  }, [
    context,
    state.balance,
    state.isBeforeVeda,
    state.selectedPlayerOnTeam,
    userInitialData,
  ]);

  const tacticString = useMemo(() => {
    return state?.tactic?.formation?.toString().replaceAll(',', '-');
  }, [state?.tactic?.formation]);

  return (
    <>
      <Loader isVisible={isLoading} />
      <Card
        className={cn(
          'card--rounded text-left flex w-full justify-between items-start font-semibold text-gdt-gray-4 text-3xs min-h-[76px]',
          {
            '!px-6 !py-2 !items-center !rounded-none': isFilterTeamVariant,
            'lg:items-center lg:text-gdt-gray-3 lg:text-base':
              !isFilterTeamVariant,
            'justify-normal': pinsData.isPremium && !isFilterTeamVariant,
          }
        )}
      >
        {!isFilterTeamVariant &&
          (context === EnumTeamContext.Team5 && isDesktop ? (
            <div className="lg:w-12 w-0" />
          ) : (
            <>
              <div className="relative inline-block">
                <span className="refButtonSelecTactic absolute left-0 h-28 w-full top-1/2 -translate-y-1/2" />
                <Button
                  className="lg:block hidden lg:w-24 lg:text-base"
                  data-tooltip-html={`Conocé cómo cambiar tu táctica`}
                  data-tooltip-id="tooltip-formacion"
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setTacticAssistant({
                      ...tacticAssistant,
                      showModalTacticSelector: true,
                    });
                  }}
                >
                  {tacticString}
                </Button>
              </div>
              <Tooltip
                className="max-w-[700px] whitespace-normal text-left !text-xs"
                float={false} // evita que cambie automáticamente
                id="tooltip-formacion"
                openEvents={{ mouseenter: true, focus: false }} // evita que se abra al devolver el foco tras cerrar los modales
                place="right" // fuerza la posición a la derecha
              />
              <Modal
                bottomSheetClassName="px-[1rem] pb-12 pt-0"
                className="px-[35px] py-[1.75rem]"
                controlledOnClose={() => {
                  setTacticAssistant({
                    ...tacticAssistant,
                    showModalTacticSelector: false,
                  });
                }}
                isControlledOpen={tacticAssistant.showModalTacticSelector}
                render={() => (
                  <TacticPicker
                    fieldSelectedTactic={state.tactic?.id ?? 1}
                    onSubmit={async (tacticId) => {
                      await onHandleTacticModal(state.tactic?.id ?? 1);
                      setTacticAssistant({
                        ...tacticAssistant,
                        showModalChanges: true,
                        showModalTacticSelector: false,
                        newTactic: tacticId as TacticsIds,
                      });
                    }}
                  />
                )}
                size="2xs"
                variant="controlled"
              />
            </>
          ))}
        <div
          className={cn('flex gap-2', {
            'lg:gap-6': !isFilterTeamVariant,
            'lg:gap-11': context === EnumTeamContext.Team5,
            'lg:flex-1 lg:justify-center lg:max-w-[calc(100%-9rem)]':
              pinsData.isPremium,
          })}
        >
          <div
            className={cn(
              !isFilterTeamVariant && 'lg:flex lg:items-center lg:gap-3'
            )}
          >
            <p className="lg:mb-0 mb-[6px] leading-none">
              {isFilterTeamVariant ? 'PRESUPUESTO DISPONIBLE' : 'PRESUPUESTO'}
            </p>
            <span
              className={cn('font-bold text-gdt-gray-2 text-lg ', {
                'lg:text-gdt-gray lg:text-xl': !isFilterTeamVariant,
              })}
            >
              {displayBalance}
            </span>
          </div>
          {(!isFilterTeamVariant || isMobile) && (
            <>
              <Divider
                className={cn('h-[2.6rem]', {
                  'lg:bg-gdt-gray-5 lg:h-[1.4rem]': !isFilterTeamVariant,
                })}
                type="vertical"
              />
              <div
                className={cn('w-[75px] refPins', {
                  'lg:w-auto lg:flex lg:items-center': !isFilterTeamVariant,
                  'w-auto': pinsData.isPremium,
                  'w-full': isFilterTeamVariant,
                })}
              >
                <div className="flex items-center relative lg:gap-2 lg:mr-4">
                  <p
                    className={cn('mb-[6px] lg:mb-0 leading-none', {
                      'h-5': isFilterTeamVariant,
                    })}
                  >
                    MIS PINES
                  </p>
                  {!isFilterTeamVariant && (
                    <FiInfo
                      className="text-gdt-blue relative lg:relative lg:right-auto left-[5px] lg:left-0 lg:top-0 -top-[3px] cursor-pointer hover:opacity-90"
                      size={18}
                      onClick={() => {
                        handleSetPinsData({
                          ...pinsData,
                          showInputsModalPins: false,
                          showModalWhatsPins: true,
                        });
                      }}
                    />
                  )}
                </div>
                <span
                  className={cn('font-bold text-gdt-gray-2 text-lg', {
                    'lg:text-xl lg:text-gdt-gray': !isFilterTeamVariant,
                  })}
                >
                  {!pinsData.isPremium ? (
                    pinsData.pinsAvailable || 0
                  ) : (
                    <div className="flex items-center gap-1">
                      <figure className="scale-125">
                        <PremiumIcon context="page" variant="premium" />
                      </figure>
                      <span className="text-xs lg:text-sm text-gdt-status-yellow">
                        ILIMITADOS
                      </span>
                    </div>
                  )}
                </span>
              </div>
            </>
          )}
        </div>
        {isFilterTeamVariant ? (
          <Button
            className={cn('py-2 flex items-center gap-x-1 text-xs', {
              'bg-[#CCD9FF] border-gdt-blue': state.showFiltersPlayersList,
            })}
            type="button"
            variant={'secondary'}
            onClick={onClick}
          >
            {(!state.showFiltersPlayersList && isDesktop) || isMobile ? (
              <>
                <GiSettingsKnobs className="w-4 h-4 rotate-90" /> Filtros
              </>
            ) : (
              <>
                <FiX className="w-4 h-4 rotate-90" /> Cerrar
              </>
            )}
          </Button>
        ) : (
          !pinsData.isPremium && (
            <Button
              className="h-[2.25rem] lg:h-[2.6rem] lg:w-[8.4rem] px-2 py-0 text-xs lg:text-sm self-center"
              type="button"
              variant={isDesktop ? 'primary' : 'secondary'}
              onClick={() => navigate(ROUTES.myAccount)}
            >
              Obtener pines
            </Button>
          )
        )}
      </Card>
      <ModalTacticChange
        currentTacticId={state.tactic?.id as TacticsIds}
        isOpen={tacticAssistant.showModalChanges}
        newTacticId={tacticAssistant.newTactic}
        tacticAssistantResponse={tacticAssistantResponse}
        onClose={() =>
          setTacticAssistant({
            ...tacticAssistant,
            showModalChanges: false,
            showModalTacticSelector: true,
          })
        }
      />
    </>
  );
};
